import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { OpenRole } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { getOpenRoles } from '@/lib/careers'
import { MaskLines, Reveal } from '@/components/motion/reveal'
import { HeroField } from '@/components/motion/hero-field'
import { ProximityText } from '@/components/motion/proximity-text'
import { ScrambleText } from '@/components/motion/scramble-text'
import { Marquee } from '@/components/motion/marquee'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ToneSection } from '@/components/generals/tone'
import { Logo } from '@/components/generals/logo'
import { Offices } from '@/components/landing/shared/offices'
import { departments } from '@/constants/content'
import { siteConfig } from '@/constants/site'

interface CareersProps {
    roles: OpenRole[]
    unavailable: boolean
}

const Careers: FC<CareersProps> = ({ roles, unavailable }): ReactNode => {
    return (
        <Layout tone='ink' footerCta={false}>
            <Seo
                title='Careers'
                description='Join nSure.ai and help merchants in crypto, gaming and prepaid and gift cards turn payment fraud prevention into a profit center. Open roles across New York, London and Tel Aviv.'
            />

            <ToneSection tone='ink' id='hero' className='isolate overflow-hidden px-5 pb-20 pt-32 md:px-8 lg:px-12 lg:pb-28 lg:pt-44'>
                <HeroField accent='#b492ff' />

                <div className='flex items-center justify-between t-label text-tone-muted'>
                    <ScrambleText text='Careers' delay={200} />
                    <span>
                        {roles.length > 0 ? `${String(roles.length).padStart(2, '0')} open ${roles.length === 1 ? 'role' : 'roles'}` : 'Always meeting good people'}
                    </span>
                </div>

                <h1 className='mt-14'>
                    <span className='sr-only'>
                        Join nSure.ai
                    </span>
                    <span aria-hidden className='flex flex-wrap items-end gap-x-[3vw] gap-y-4'>
                        <MaskLines
                            as='span'
                            immediate
                            className='block font-heading text-[clamp(5rem,19vw,20rem)] font-semibold uppercase leading-[0.78] tracking-[-0.05em] wdth-62'
                            lines={[<ProximityText key='join' text='Join' minWidth={62} maxWidth={125} />]}
                        />
                        <MaskLines
                            as='span'
                            immediate
                            delay={0.15}
                            className='block w-[min(88vw,58rem)] pb-[1.5vw]'
                            lines={[<Logo key='logo' className='h-auto w-full' title='nSure.ai' />]}
                        />
                    </span>
                </h1>

                <div className='mt-16 grid gap-10 lg:mt-24 lg:grid-cols-12'>
                    <Reveal className='t-lead lg:col-span-6'>
                        We build the payment fraud prevention provider of choice for gaming, prepaid and gift cards, and crypto, turning fraud prevention into a profit driver for crypto exchanges and high-risk digital merchants.
                    </Reveal>
                    <Reveal className='text-tone-muted lg:col-span-4 lg:col-start-9' delay={0.1}>
                        Teams across revenue, engineering, data, fraud operations, product and more, working from New York, London and Tel Aviv.
                    </Reveal>
                </div>
            </ToneSection>

            <ToneSection tone='void' id='open-roles' className='px-5 py-28 md:px-8 lg:px-12 lg:py-36'>
                <div className='mb-14 flex flex-wrap items-end justify-between gap-6 lg:mb-20'>
                    <MaskLines
                        as='h2'
                        className='font-heading t-h1 font-semibold uppercase wdth-75'
                        lines={['Open roles']}
                    />
                    <span className='t-label text-tone-muted'>
                        Updated from our careers board
                    </span>
                </div>

                {roles.length > 0 ? (
                    <ul className='border-b border-tone-line'>
                        {roles.map((role, index) => (
                            <li key={role.uid}>
                                <a
                                    href={role.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    data-cursor='Apply'
                                    className='group grid gap-4 border-t border-tone-line py-7 md:grid-cols-12 md:items-center md:py-9'
                                >
                                    <span className='t-label text-tone-muted md:col-span-1'>
                                        {String(index + 1).padStart(2, '0')}
                                    </span>
                                    <span className='font-heading text-[clamp(1.75rem,3.4vw,3.25rem)] font-semibold leading-[0.95] tracking-[-0.03em] transition-colors duration-500 group-hover:text-tone-accent md:col-span-6'>
                                        {role.name}
                                    </span>
                                    <span className='flex flex-wrap gap-x-6 gap-y-2 t-label text-tone-muted md:col-span-4'>
                                        {role.department && (
                                            <span>
                                                {role.department}
                                            </span>
                                        )}
                                        {role.location && (
                                            <span>
                                                {role.location}
                                            </span>
                                        )}
                                        {role.workplace && (
                                            <span className='text-tone-accent'>
                                                {role.workplace}
                                            </span>
                                        )}
                                    </span>
                                    <span className='flex md:col-span-1 md:justify-end'>
                                        <span className='flex size-12 items-center justify-center border border-tone-line transition-colors duration-500 group-hover:bg-tone group-hover:text-tone-bg'>
                                            <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:rotate-45' aria-hidden />
                                        </span>
                                    </span>
                                </a>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className='flex flex-col gap-6 border-y border-tone-line py-12 md:flex-row md:items-center md:justify-between'>
                        <p className='max-w-xl t-lead text-tone-muted'>
                            {unavailable
                                ? 'Our list of open roles is not loading right now.'
                                : 'There are no open roles at the moment.'}{' '}
                            We’re always happy to hear from people who want to fight payment fraud with us.
                        </p>
                        <a href={`mailto:${siteConfig.email}?subject=${encodeURIComponent('Careers at nSure.ai')}`} className='link-line self-start text-lg font-semibold md:self-auto'>
                            {siteConfig.email}
                        </a>
                    </div>
                )}
            </ToneSection>

            <ToneSection tone='paper' id='teams' className='overflow-hidden py-24 lg:py-32'>
                <p className='mb-10 px-5 t-label text-tone-muted md:px-8 lg:px-12'>
                    Our teams
                </p>
                <Marquee speed={2}>
                    {departments.map(department => (
                        <span key={department} className='flex shrink-0 items-center gap-[4vw] pr-[4vw] font-heading text-[clamp(3.5rem,9vw,9rem)] font-semibold uppercase leading-none tracking-[-0.04em] wdth-75'>
                            {department}
                            <span className='size-[0.18em] rounded-full bg-violet' aria-hidden />
                        </span>
                    ))}
                </Marquee>
                <Marquee speed={-1.4} className='mt-4'>
                    {departments.map(department => (
                        <span key={department} className='flex shrink-0 items-center gap-[4vw] pr-[4vw] font-heading text-[clamp(3.5rem,9vw,9rem)] font-semibold uppercase leading-none tracking-[-0.04em] text-outline wdth-112'>
                            {department}
                            <span className='size-[0.18em] rounded-full border border-current' aria-hidden />
                        </span>
                    ))}
                </Marquee>
            </ToneSection>

            <Offices tone='lilac' />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<CareersProps> = async () => {
    try {
        const roles = await getOpenRoles()

        return { props: { roles, unavailable: false }, revalidate: 900 }
    }
    catch (error) {
        console.error('Open roles could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { roles: [], unavailable: true }, revalidate: 120 }
    }
}

export default Careers
